import React, { useState, useMemo, useCallback } from "react";
import Model from "@tripian/model";
import {
  TripCard,
  ConfirmModalPopup,
  TabMenu,
  PreLoading,
} from "@tripian/react";
import moment from "moment";
import { api } from "@tripian/core";
import { useDispatch, useSelector } from "react-redux";
import { saveNotification } from "../../redux/action/trip";
import ICombinedState from "../../redux/model/ICombinedState";
import useTimeline from "../../hooks/useTimeline";
import ShareTripModal from "../ShareTripModal/ShareTripModal";
import useTranslate from "../../hooks/useTranslate";
import classes from "./TimelineTripList.module.scss";

interface ITimelineTripList {
  timelineTrips: Model.TimelineTrip[];
  loadingTimelineTrips: boolean;
  cardClicked: (tripHash: string) => void;
  cardEditClicked: (tripHash: string) => void;
  cardDeleteClicked: (tripHash: string) => Promise<boolean>;
  tripCreateClicked: () => void;
}

const TimelineTripList: React.FC<ITimelineTripList> = ({
  timelineTrips,
  loadingTimelineTrips,
  cardClicked,
  cardEditClicked,
  cardDeleteClicked,
  tripCreateClicked,
}) => {

  const [modalState, setModalState] = useState({ show: false, tripHash: "" });
  const [isPastTrip, setIsPastTrip] = useState(false);
  const [icsLoading, setIcsLoading] = useState<{ tripHashes: string[] }>({
    tripHashes: [],
  });
  const [searchText, setSearchText] = useState("");

  const { t } = useTranslate();

  const { timelineShare } = useTimeline();
  const { shared } = useSelector((state: ICombinedState) => ({
    shared: state.trip.reference?.shared,
  }));

  const [shareModalState, setShareModalState] = useState<{
    show: boolean;
    loading: boolean;
    switchChecked: boolean;
    timelineTrip?: Model.TimelineTrip;
  }>({
    show: false,
    loading: false,
    switchChecked: shared || false,
    timelineTrip: undefined,
  });

  const dispatch = useDispatch();

  const timelineTripsToShow = useMemo(() => {
    const now = moment(new Date()).format("X");

    return timelineTrips
      .filter(trip => {
        const segments = trip.tripProfile?.segments || [];
        const lastSegment = segments[segments.length - 1];
        const endDate = lastSegment?.endDate;
        if (!endDate) return true;

        const tripEndDate = moment(endDate).utcOffset(0).format("X");
        return isPastTrip ? tripEndDate < now : tripEndDate > now;
      })
      .filter(
        trip =>
          trip.city.name
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          trip.city.name.toLowerCase().includes(searchText.toLowerCase())
      );
  }, [isPastTrip, timelineTrips, searchText]);

  const modalFinally = useCallback(() => {
    setModalState({ show: false, tripHash: "" });
  }, []);

  const icsFileDownload = (tripHash: string) => {
    const newIcsLoading = { ...icsLoading };
    newIcsLoading.tripHashes.push(tripHash);
    setIcsLoading(newIcsLoading);
    api
      .tripDownloadIcs(tripHash)
      .catch(err => {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "planDownload", err)
        );
      })
      .finally(() => {
        const newIcsLoading = { ...icsLoading };
        const tripHashIndex = newIcsLoading.tripHashes.findIndex(
          t => t === tripHash
        );
        newIcsLoading.tripHashes.splice(tripHashIndex, 1);
        setIcsLoading(newIcsLoading);
      });
  };

  const switchCheckedOnchange = (checked: boolean, tripHash?: string) => {
    if (tripHash) {
      setShareModalState({ ...shareModalState, loading: true });
      timelineShare(tripHash, checked).finally(() => {
        setShareModalState({
          ...shareModalState,
          switchChecked: checked,
          loading: false,
        });
      });
    }
  };

  // Convert TimelineTrip to TripReference format for TripCard compatibility
  const convertToTripReference = (timelineTrip: Model.TimelineTrip): Model.TripReference => {
    const segments = timelineTrip.tripProfile?.segments || [];
    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];
    return {
      id: timelineTrip.id,
      tripHash: timelineTrip.tripHash,
      shared: timelineTrip.shared,
      city: timelineTrip.city,
      tripProfile: {
        cityId: timelineTrip.city.id,
        arrivalDatetime: firstSegment?.startDate || moment().format("YYYY-MM-DD HH:mm"),
        departureDatetime: lastSegment?.endDate || moment().add(1, "day").format("YYYY-MM-DD HH:mm"),
        numberOfAdults: timelineTrip.tripProfile?.adults || 1,
        numberOfChildren: timelineTrip.tripProfile?.children || 0,
        accommodation: null,
        answers: timelineTrip.tripProfile?.answerIds || [],
        companionIds: [],
        excludeHash: timelineTrip.tripProfile?.excludeHashPois || [],
        doNotGenerate: 0,
        tripName: (timelineTrip.tripProfile as any)?.cityName || timelineTrip.city.name,
      },
    };
  };

  return (
    <>
      <div className="col col12" style={{ padding: "0 10%" }}>
        <TabMenu
          menuItems={[
            {
              title: t("trips.myTrips.upComingTrips.title"),
              onClick: () => {
                setIsPastTrip(false);
              },
            },
            {
              title: t("trips.myTrips.pastTrips.title"),
              onClick: () => {
                setIsPastTrip(true);
              },
            },
          ]}
          selectedIndex={0}
        />
      </div>

      {loadingTimelineTrips ? (
        <PreLoading customClass="py8" />
      ) : (
        <>
          <ConfirmModalPopup
            openModalPopup={modalState.show}
            text={t("trips.deleteTrip.question")}
            confirmButtonText={t("trips.deleteTrip.submit")}
            cancelButtonText={t("trips.deleteTrip.cancel")}
            action={() => cardDeleteClicked(modalState.tripHash)}
            confirmationCallback={modalFinally}
            cancelCallback={modalFinally}
            className={classes.comfirmModal}
          />

          {shareModalState?.show && shareModalState.timelineTrip && (
            <ShareTripModal
              onCancel={() =>
                setShareModalState({ ...shareModalState, show: false })
              }
              loading={shareModalState.loading}
              switchChecked={shareModalState.switchChecked}
              onChange={switchCheckedOnchange}
              cityName={shareModalState.timelineTrip.city.name}
              arrivalDatetime={
                shareModalState.timelineTrip.plans?.[0]?.startDate
              }
              departureDatetime={
                shareModalState.timelineTrip.plans?.[
                  shareModalState.timelineTrip.plans.length - 1
                ]?.endDate
              }
              tripHash={shareModalState.timelineTrip.tripHash}
              dayIndex={0}
            />
          )}

          <div className="flex col col12 center mt-4 !mb-10">
            <div className="flex items-center border rounded-full px-4 py-2 w-full max-w-lg shadow-sm bg-[#f5f5f5] justify-self-center">
              <svg
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-6 h-6 text-[#61656E] mr-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 103.5 10.5a7.5 7.5 0 0013.15 6.15z"
                />
              </svg>
              <input
                type="text"
                placeholder={t("trips.searchPlaceHolder")}
                className="outline-none w-full text-base py-2 bg-[#f5f5f5]"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {timelineTripsToShow.length === 0 ? (
            <>
              <div className="col col12 mt-12 text-2xl font-medium font-inter">
                {isPastTrip
                  ? t("trips.myTrips.pastTrips.emptyMessage")
                  : t("trips.myTrips.upComingTrips.emptyMessage")}
              </div>
            </>
          ) : (
            <div className="row gap-y-12">
              {timelineTripsToShow.map((timelineTrip, i) => {
                const tripReference = convertToTripReference(timelineTrip);
                  return (
                  <div key={timelineTrip.id} className="col col12 col6-m">
                    <TripCard
                      tripReference={tripReference}
                      clicked={() => cardClicked(timelineTrip.tripHash)}
                      editTrip={() => {
                          cardEditClicked(timelineTrip.tripHash);
                      }}
                      deleteTrip={hash => {
                        setModalState({ show: true, tripHash: hash });
                      }}
                      icsFileDownload={icsFileDownload}
                      icsLoading={icsLoading.tripHashes.some(
                        tripHash => tripHash === timelineTrip.tripHash
                      )}
                      showShareTrip={window.tconfig.SHARED_TRIP}
                      shareTrip={() => {
                        setShareModalState({
                          ...shareModalState,
                          show: true,
                          loading: false,
                          timelineTrip,
                          switchChecked: timelineTrip.shared,
                        });
                      }}
                      t={t}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TimelineTripList;
